/**
 * The sheet's foot — the two acts that end the session, and the one that takes pins back.
 *
 * ── „Zastosuj do draftu" carries mobile's own name ──────────────────────────────────────────
 * `plans.progression.applyCta.label` from the VERBATIM locale, so one Playwright locator finds this
 * button and mobile's in the same words. The same holds for the keyboard hint's verbs.
 *
 * ── „Wyczyść nadpisania" is a separate act because a pin is a separate act ──────────────────
 * Pins are written straight to the draft by the matrix (see `lib/useProgressionSheet.ts`), each with
 * its own undo entry, so „Anuluj" has nothing of theirs to discard. The mock draws this button for
 * exactly that reason. It clears them through `clearExercisePins`, which is ONE draft operation and
 * one undo entry — reverting cell by cell would leave an undo that walks the grid back a square at a
 * time, which is not what the button says it does. The axis is the exercise's own, derived inside the
 * operation, because a pin's axis may never come from the surface asking.
 */

import { clearExercisePins, hasOverrides, type ExerciseDraft } from '~/entities/plan-draft';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import type { Translate } from '../model/matrixCellState';

import styles from './ProgressionSheet.module.css';

const KEY = 'web:planBuilder.progression.sheet';

interface SheetFootProps {
  readonly stored: ExerciseDraft;
  readonly isDirty: boolean;
  /**
   * The GRID holds focus. The two keys this hint names are the cell field's — Enter opens and
   * commits a cell, Escape hands it back to the rule — and Escape anywhere else in the sheet closes
   * the sheet, which „oddaj regule" does not describe. So it is printed where it is true.
   */
  readonly showKeys: boolean;
  readonly onClearPins: () => void;
  readonly onCancel: () => void;
  readonly onApply: () => void;
  readonly t: Translate;
}

export function SheetFoot({
  stored,
  isDirty,
  showKeys,
  onClearPins,
  onCancel,
  onApply,
  t,
}: SheetFootProps) {
  const hasPins = hasOverrides(stored.progressionOverrides);

  return (
    <div className={styles.foot}>
      <button
        type="button"
        className={`${String(ghost.ghost)} ${String(styles.footButton)}`}
        /* Focusable while refused: the name is where the reason lives, and a control dropped out of
          * the tab order takes its own explanation with it. */
        aria-disabled={!hasPins}
        aria-label={hasPins ? undefined : `${t(`${KEY}.foot.clearPins`)}, ${t(`${KEY}.applyNothing`)}`}
        onClick={() => {
          if (!hasPins) return;
          clearExercisePins(stored.slotToken);
          onClearPins();
        }}
      >
        {t(`${KEY}.foot.clearPins`)}
      </button>

      {showKeys && (
        <span className={styles.kbdHint}>
          <i className={styles.kbd}>⏎</i> {t(`${KEY}.foot.kbdCommit`)}
          <i className={styles.kbd}>Esc</i> {t(`${KEY}.foot.kbdRevert`)}
        </span>
      )}

      <span className={styles.spring} />

      <button
        type="button"
        className={`${String(ghost.ghost)} ${String(styles.footButton)}`}
        onClick={onCancel}
      >
        {t(`${KEY}.foot.cancel`)}
      </button>
      <button
        type="button"
        className={`${String(ink.ink)} ${String(styles.footPrimary)}`}
        aria-disabled={!isDirty}
        aria-label={isDirty ? undefined : `${t('plans.progression.applyCta.label')}, ${t(`${KEY}.applyNothing`)}`}
        onClick={() => {
          if (isDirty) onApply();
        }}
      >
        {t('plans.progression.applyCta.label')}
      </button>
    </div>
  );
}
